import { stringUtils } from '@pukka/common/utils';
import { Accordion, Checkbox, Icon, Switch, Text, Tooltip } from '@pukka/ui-components';

import { BillingPriceItem } from './BillingPriceItem';
import { IPaymentState } from './BillingReceipt.state';

interface BillingPriceDetailProps {
  readonly isLoading: boolean;
  readonly disabled?: boolean;
  readonly outlets: IPaymentState['amount']['outlets'];
  readonly employeeSlots: IPaymentState['amount']['employeeSlots'];
  readonly isEnterprise?: boolean;
  readonly onToggleOutletSelection: IPaymentState['onToggleOutletSelection'];
}

function BillingPriceDetail({
  isLoading,
  disabled,
  outlets,
  employeeSlots,
  isEnterprise,
  onToggleOutletSelection,
}: BillingPriceDetailProps) {
  const totalActiveOutlets = outlets?.filter((outlet) => outlet.isSelected).length;

  return (
    <Accordion.Section.Root
      disabled={isLoading || disabled}
      type="single"
      collapsible
      className="outline-none view-transition-[price-detail]"
    >
      <Accordion.Section.Item value="outlets">
        <Accordion.Section.Header>
          <div className="text-left">
            <Text variant="subtitle-2" weight="medium" className="text-neutral-700">
              Outlets ({totalActiveOutlets})
            </Text>
            <Text variant="caption-2" className="mt-0.5 text-neutral-600">
              Expand for detail payment per outlet.
            </Text>
          </div>
        </Accordion.Section.Header>
        <Accordion.Section.Content>
          <div className="flex flex-col">
            {/* Staff slots */}
            {employeeSlots.count ? (
              <BillingPriceItem
                key="staff"
                loading={isLoading}
                title="Staff Slots"
                subTitle={stringUtils.pluralize(employeeSlots.count, 'Slot')}
                originalPrice={employeeSlots.originalPrice}
                finalPrice={employeeSlots.finalPrice}
                priceSuffix={employeeSlots.unit}
                hidePrice={isEnterprise}
              >
                <Checkbox disabled checked size="lg" className="block text-center" />
              </BillingPriceItem>
            ) : null}

            {/* Outlet list */}
            {outlets.map((outlet) => {
              const isActive = outlet.isSelected;
              const disableToggle = isActive && totalActiveOutlets <= 1;

              return (
                <div key={outlet.id}>
                  <BillingPriceItem
                    key={outlet.id}
                    loading={isLoading}
                    title={outlet.name}
                    subTitle={outlet.description}
                    note={isEnterprise ? '' : outlet.prorateInfo}
                    originalPrice={outlet.originalPrice}
                    finalPrice={outlet.finalPrice}
                    priceSuffix={outlet.unit}
                    hidePrice={isEnterprise}
                  >
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {isEnterprise ? (
                      <div className="text-center">
                        <Icon icon="blocked" size="xl" className="text-neutral-300" />
                      </div>
                    ) : disableToggle ? (
                      <Tooltip
                        trigger={
                          <div className="w-fit">
                            <Switch checked={isActive} disabled size="lg" />
                          </div>
                        }
                      >
                        <Text color="white" variant="caption-1">
                          at least one outlet needs to be active
                        </Text>
                      </Tooltip>
                    ) : (
                      <Switch
                        disabled={isLoading || disabled || disableToggle}
                        size="lg"
                        checked={isActive}
                        // eslint-disable-next-line react/jsx-no-bind
                        onCheckedChange={onToggleOutletSelection.bind({}, outlet.id)}
                      />
                    )}
                  </BillingPriceItem>
                </div>
              );
            })}
          </div>
        </Accordion.Section.Content>
      </Accordion.Section.Item>
    </Accordion.Section.Root>
  );
}

export { BillingPriceDetail };
